import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "@/lib/use-toast";
import type { MeasurementRecordCreate, MeasurementValueSummary, MeasurementValueUpdate, PatientMeasurementsResponse } from "@/types/measurement";
import { archiveMeasurementValue, getFieldTrend, getFieldsPrefill, getGroupPrefill, getPatientMeasurements, recordMeasurements, updateMeasurementValue } from "@/api/provider/user/patient/measurement";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";
import { invalidateByKey } from "@/api/utils/query-helpers";
import { measurementFieldQueryOptions } from "@/query/settings/measurement-fields";
import { measurementGroupQueryOptions } from "@/query/settings/measurement-groups";
export const measurementQueryKeys = {
  all: ["user", "patient", "measurements"] as const,
  patient: (patientId: string) => [...measurementQueryKeys.all, patientId] as const,
  trend: (patientId: string, fieldId: string) => [...measurementQueryKeys.patient(patientId), "trend", fieldId] as const,
  prefill: (patientId: string, groupId: string | null, fieldIds: string[], start: string, end: string) => [...measurementQueryKeys.patient(patientId), "prefill", groupId ?? "fields", [...fieldIds].sort().join(","), start, end] as const
};
export const measurementQueryOptions = {
  patient: (patientId: string) => ({
    queryKey: measurementQueryKeys.patient(patientId),
    queryFn: () => getPatientMeasurements(patientId),
    select: (data: PatientMeasurementsResponse | null) => data ?? {
      values: []
    },
    enabled: !!patientId
  }),
  trend: (patientId: string, fieldId: string) => ({
    queryKey: measurementQueryKeys.trend(patientId, fieldId),
    queryFn: () => getFieldTrend(patientId, fieldId),
    select: (data: MeasurementValueSummary[] | null) => data ?? [],
    enabled: !!patientId && !!fieldId
  }),
  prefill: (patientId: string, groupId: string | null, fieldIds: string[], start: string, end: string) => ({
    queryKey: measurementQueryKeys.prefill(patientId, groupId, fieldIds, start, end),
    queryFn: () => groupId ? getGroupPrefill(patientId, groupId, start, end) : getFieldsPrefill(patientId, fieldIds, start, end),
    enabled: !!patientId && !!start && !!end && (!!groupId || fieldIds.length > 0)
  })
};
export function usePatientMeasurementsQuery(patientId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = measurementQueryOptions.patient(patientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useQuery(t0);
}

/** Combines patient values with tenant-wide groups and fields from settings. */
export function usePatientMeasurementData(patientId) {
  const $ = _c(13);
  const {
    data: patientData,
    isLoading: isLoadingValues
  } = usePatientMeasurementsQuery(patientId);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = measurementGroupQueryOptions.list();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: groups,
    isLoading: isLoadingGroups
  } = useQuery(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = measurementFieldQueryOptions.list();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: fields,
    isLoading: isLoadingFields
  } = useQuery(t1);
  let t2;
  if ($[2] !== groups) {
    t2 = groups ?? [];
    $[2] = groups;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== fields) {
    t3 = fields ?? [];
    $[4] = fields;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== patientData?.values) {
    t4 = patientData?.values ?? [];
    $[6] = patientData?.values;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isLoadingValues || isLoadingGroups || isLoadingFields;
  let t6;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = {
      groups: t2,
      fields: t3,
      values: t4,
      isLoading: t5
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
export function useMeasurementTrendQuery(patientId, fieldId) {
  const $ = _c(3);
  let t0;
  if ($[0] !== fieldId || $[1] !== patientId) {
    t0 = measurementQueryOptions.trend(patientId, fieldId);
    $[0] = fieldId;
    $[1] = patientId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useQuery(t0);
}
export const useRecordMeasurementsMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onError: _temp2,
      onSettled: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateMeasurementValueMutation = patientId => {
  const $ = _c(7);
  let t0;
  if ($[0] !== patientId) {
    t0 = t1 => {
      const {
        valueId,
        data
      } = t1;
      return updateMeasurementValue(patientId, valueId, data);
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId) {
    t1 = () => {
      invalidateByKey(measurementQueryKeys.patient(patientId));
    };
    $[2] = patientId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: t0,
      onError: _temp4,
      onSettled: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
};
export const useArchiveMeasurementValueMutation = patientId => {
  const $ = _c(7);
  let t0;
  if ($[0] !== patientId) {
    t0 = valueId => archiveMeasurementValue(patientId, valueId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId) {
    t1 = () => {
      invalidateByKey(measurementQueryKeys.patient(patientId));
    };
    $[2] = patientId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: t0,
      onError: _temp5,
      onSettled: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
};
function _temp(data) {
  return recordMeasurements(data);
}
function _temp2() {
  toast({
    title: "Error",
    description: "Failed to record measurements",
    variant: "error"
  });
}
function _temp3(_res, _err, vars) {
  invalidateByKey(measurementQueryKeys.patient(vars.patientId));
  invalidateByKey(patientQueryKeys.detail(vars.patientId));
}
function _temp4() {
  toast({
    title: "Error",
    description: "Failed to update measurement",
    variant: "error"
  });
}
function _temp5() {
  toast({
    title: "Error",
    description: "Failed to delete measurement",
    variant: "error"
  });
}